import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Popover,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import {
    PickersDay,
    PickersDayProps,
} from "@mui/x-date-pickers/PickersDay";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import axios from "../../../utils/axiosInstance";
import React, { useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { MILLIS_TO_LOCALE_DIGIT_FORMAT } from "../../../utilities/helpers/formatUnix";
import SearchIcon from "@mui/icons-material/Search";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { METADATA_MANAGEMENT } from "../LandingPage/constants";
import MetadataForm from "./MetadataForm";

const MAX_RANGE_MONTHS = 6;

interface AirtableTitleRow {
    refId: string;
    name: string;
    contentType: string;
    startDate: string;
    endDate: string;
}

let rangeStart: Dayjs | null = null;
let rangeEnd: Dayjs | null = null;

const RangeDay = (props: PickersDayProps<Dayjs>) => {
    const { day, outsideCurrentMonth, ...other } = props;

    const isStart = !!rangeStart && day.isSame(rangeStart, "day");
    const isEnd = !!rangeEnd && day.isSame(rangeEnd, "day");
    const isBetween =
        !!rangeStart &&
        !!rangeEnd &&
        day.isAfter(rangeStart, "day") &&
        day.isBefore(rangeEnd, "day");

    return (
        <PickersDay
            {...other}
            day={day}
            outsideCurrentMonth={outsideCurrentMonth}
            sx={(theme) => ({
                ...(isBetween && {
                    bgcolor: theme.palette.primary.light,
                    color: theme.palette.primary.contrastText,
                    borderRadius: 0,
                }),
                ...(isStart && {
                    borderTopLeftRadius: theme.shape.borderRadius,
                    borderBottomLeftRadius: theme.shape.borderRadius,
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                }),
                ...(isEnd && {
                    borderTopRightRadius: theme.shape.borderRadius,
                    borderBottomRightRadius: theme.shape.borderRadius,
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                }),
            })}
        />
    );
};

function AirtableLogoIcon() {
    return (
        <Box
            component="span"
            sx={{
                display: "inline-flex",
                width: 20,
                height: 20,
                mr: 0.5,
                "& svg": { width: "100%", height: "100%" },
            }}
        >
            <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
            >
                <path
                    d="M5 3h4v4H5V3zm0 6h4v4H5V9zm0 6h4v4H5v-4zm6-12h4v4h-4V3zm0 6h4v4h-4V9zm0 6h4v4h-4v-4zm6-12h4v4h-4V3zm0 6h4v4h-4V9zm0 6h4v4h-4v-4z"
                    fill="currentColor"
                />
            </svg>
        </Box>
    );
}

function normalizeAirtableRow(raw: any): AirtableTitleRow {
    return {
        refId:
            raw.refId ??
            "",
        name: raw.name ?? "",
        contentType:
            raw.content_type ??
            "",
        startDate:
            raw.start_date ??
            "",
        endDate:
            raw.end_date ??
            "",
    };
}

const AIRTABLE_PREVIEW_URL = `${process.env.BASE_URL || ""}api/v1/cms/avails/airtable/preview`;
const AIRTABLE_DOWNLOAD_SPREADSHEET_URL = `${process.env.BASE_URL || ""}api/v1/cms/avails/airtable/download-spreadsheet`;

async function fetchTitlesFromAirtable(
    startDate: string,
    endDate: string
): Promise<AirtableTitleRow[]> {
    const res = await axios.post(AIRTABLE_PREVIEW_URL, {
        startDate,
        endDate,
    });
    const list =
        res?.data?.data?.titles ??
        res?.data?.data?.list ??
        res?.data?.titles ??
        res?.data?.list ??
        [];
    const arr = Array.isArray(list) ? list : [];
    return arr.map(normalizeAirtableRow);
}

async function downloadEmaAvailsSpreadsheet(
    startDate: string,
    endDate: string
): Promise<{ blob: Blob; headers: Record<string, string> }> {
    const res = await axios.post(
        AIRTABLE_DOWNLOAD_SPREADSHEET_URL,
        { startDate, endDate },
        { responseType: "blob" }
    );
    const headers: Record<string, string> = {};
    if (res.headers && typeof res.headers === "object") {
        Object.entries(res.headers).forEach(([k, v]) => {
            if (typeof v === "string") headers[k.toLowerCase()] = v;
        });
    }
    return { blob: res.data as Blob, headers };
}

function getDownloadFilenameFromHeaders(headers: any): string | null {
    const disposition = headers?.["content-disposition"];
    if (typeof disposition !== "string") return null;
    const regex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const match = regex.exec(disposition);
    if (match?.[1]) {
        return match[1].replace(/['"]/g, "").trim();
    }
    return null;
}

function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function getStateColor(state: string | undefined): string {
    const s = state?.toLowerCase();
    if (s === "active") return "success.main";
    if (s === "inactive") return "error.main";
    return "text.primary";
}

type SortOrder = "asc" | "desc" | null;

const MetadataManagement = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loader, setLoader] = useState(false);
    const [searchKey, setSearchKey] = useState("");
    const [contentList, setContentList] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [skip, setSkip] = useState(METADATA_MANAGEMENT.DEFAULT_SKIP);
    const [rowsPerPage, setRowsPerPage] = useState(
        METADATA_MANAGEMENT.DEFAULT_LIMIT
    );
    const [noResult, setNoResult] = useState(false);
    const [formValue, setFormValue] = useState(null);
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortOrder, setSortOrder] = useState<SortOrder>(null);

    const [filterDateFrom, setFilterDateFrom] = useState<Dayjs | null>(null);
    const [filterDateTo, setFilterDateTo] = useState<Dayjs | null>(null);
    const [contentLicensor, setContentLicensor] = useState("");
    const [dateRangeAnchorEl, setDateRangeAnchorEl] =
        useState<HTMLElement | null>(null);

    const [fetchDialogOpen, setFetchDialogOpen] = useState(false);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [airtableStartDate, setAirtableStartDate] = useState<Dayjs | null>(
        null
    );
    const [airtableEndDate, setAirtableEndDate] = useState<Dayjs | null>(null);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [previewRows, setPreviewRows] = useState<AirtableTitleRow[]>([]);
    const [airtableLoading, setAirtableLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [previewStartDate, setPreviewStartDate] = useState<string>("");
    const [previewEndDate, setPreviewEndDate] = useState<string>("");

    const metadataFomRef = useRef<any>(null);

    const scrollToBottom = () => {
        if (metadataFomRef.current) {
            metadataFomRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    const getFilterDateRangeLabel = () => {
        if (filterDateFrom && filterDateTo) {
            return `${filterDateFrom.format("YYYY-MM-DD")} - ${filterDateTo.format(
                "YYYY-MM-DD"
            )}`;
        }
        if (filterDateFrom) {
            return `${filterDateFrom.format("YYYY-MM-DD")} -`;
        }
        if (filterDateTo) {
            return `- ${filterDateTo.format("YYYY-MM-DD")}`;
        }
        return "";
    };

    // Expose current range to the custom day component
    rangeStart = filterDateFrom;
    rangeEnd = filterDateTo;

    const handleRangeCalendarChange = (day: Dayjs | null) => {
        if (!day) return;

        // If no start selected or both start and end are already set, start a new range
        if (!filterDateFrom || (filterDateFrom && filterDateTo)) {
            setFilterDateFrom(day.startOf("day"));
            setFilterDateTo(null);
            return;
        }

        // If selecting a date before the current start, move the start
        if (day.isBefore(filterDateFrom, "day")) {
            setFilterDateFrom(day.startOf("day"));
            return;
        }

        // Otherwise set / move the end
        setFilterDateTo(day.startOf("day"));
    };

    useEffect(() => {
        onSearchAPIcall();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = (e: any) => {
        setSearchKey(e.target.value);
    };

    const handleKeyDown = (event: any) => {
        if (event.key === "Enter") {
            setSkip(METADATA_MANAGEMENT.DEFAULT_SKIP);
            onSearchAPIcall({ skipValue: METADATA_MANAGEMENT.DEFAULT_SKIP });
        }
    };

    const clearSearch = () => {
        setSearchKey("");
        setSkip(METADATA_MANAGEMENT.DEFAULT_SKIP);
        onSearchAPIcall({
            skipValue: METADATA_MANAGEMENT.DEFAULT_SKIP,
            searchKeyValue: "",
        });
    };

    const applyFilters = () => {
        setSkip(METADATA_MANAGEMENT.DEFAULT_SKIP);
        onSearchAPIcall({
            skipValue: METADATA_MANAGEMENT.DEFAULT_SKIP,
            filterDateFromValue: filterDateFrom,
            filterDateToValue: filterDateTo,
            contentLicensorValue: contentLicensor,
        });
    };

    const clearFilters = () => {
        setFilterDateFrom(null);
        setFilterDateTo(null);
        setContentLicensor("");
        setSkip(METADATA_MANAGEMENT.DEFAULT_SKIP);
        onSearchAPIcall({
            skipValue: METADATA_MANAGEMENT.DEFAULT_SKIP,
            filterDateFromValue: null,
            filterDateToValue: null,
            contentLicensorValue: "",
        });
    };

    const handleSort = (column: string) => {
        let newSortOrder: SortOrder;

        if (sortColumn === column) {
            // Cycle through: asc -> desc -> null
            if (sortOrder === "asc") {
                newSortOrder = "desc";
            } else if (sortOrder === "desc") {
                newSortOrder = null;
            } else {
                newSortOrder = "asc";
            }
        } else {
            // New column, start with asc
            newSortOrder = "asc";
        }

        setSortColumn(newSortOrder ? column : null);
        setSortOrder(newSortOrder);
        setSkip(METADATA_MANAGEMENT.DEFAULT_SKIP);
        onSearchAPIcall({
            skipValue: METADATA_MANAGEMENT.DEFAULT_SKIP,
            sortCol: column,
            sortOrd: newSortOrder,
        });
    };

    const onSearchAPIcall = async (opts?: {
        skipValue?: number;
        rowsPerPageValue?: number;
        sortCol?: string | null;
        sortOrd?: SortOrder;
        searchKeyValue?: string;
        filterDateFromValue?: Dayjs | null;
        filterDateToValue?: Dayjs | null;
        contentLicensorValue?: string;
    }) => {
        const {
            skipValue = skip,
            rowsPerPageValue = rowsPerPage,
            sortCol = sortColumn,
            sortOrd = sortOrder,
            searchKeyValue = searchKey,
            filterDateFromValue = filterDateFrom,
            filterDateToValue = filterDateTo,
            contentLicensorValue = contentLicensor,
        } = opts ?? {};
        try {
            setLoader(true);

            let url = `${process.env.BASE_URL}api/v1/cms/metadata/search?searchString=${encodeURIComponent(searchKeyValue)}&offset=${skipValue}&limit=${rowsPerPageValue}`;

            if (sortCol && sortOrd) {
                const sortPrefix = sortOrd === "desc" ? "-" : "";
                url += `&sort=${sortPrefix}${sortCol}`;
            }
            if (filterDateFromValue?.isValid()) {
                url += `&dateFrom=${filterDateFromValue.format("YYYY-MM-DD")}`;
            }
            if (filterDateToValue?.isValid()) {
                url += `&dateTo=${filterDateToValue.format("YYYY-MM-DD")}`;
            }
            if (contentLicensorValue?.trim()) {
                url += `&contentLicensor=${encodeURIComponent(contentLicensorValue.trim())}`;
            }

            const searchResult = await axios.get(url);
            setContentList(searchResult.data?.data?.list || []);
            setNoResult(!searchResult.data?.data?.list?.length);
            setTotalCount(searchResult.data?.data?.count || 0);
        } catch (error) {
            console.log("onSearchAPIcall", error);
        } finally {
            setLoader(false);
        }
    };

    const handleChangePage = async (event: unknown, newPage: number) => {
        const skipvalue = newPage * rowsPerPage;
        setSkip(skipvalue);
        onSearchAPIcall({ skipValue: skipvalue });
    };

    const handleChangeRowsPerPage = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const limit = Number.parseInt(event.target.value, 10);
        setRowsPerPage(limit);
        setSkip(0);
        onSearchAPIcall({ skipValue: 0, rowsPerPageValue: limit });
    };

    const handleSelectRecord = (rowValues: any) => {
        setFormValue(rowValues);
        scrollToBottom();
    };

    const refreshList = () => {
        onSearchAPIcall();
    };

    const renderSortLabel = (column: string, label: string) => {
        return (
            <TableSortLabel
                active={sortColumn === column}
                direction={
                    sortColumn === column && sortOrder ? sortOrder : "asc"
                }
                onClick={() => handleSort(column)}
                hideSortIcon={sortColumn !== column}
            >
                <h3>{label}</h3>
            </TableSortLabel>
        );
    };

    const validateAirtableDates = (): string | null => {
        if (!airtableStartDate?.isValid()) {
            return "Start date is required.";
        }
        if (airtableEndDate?.isValid()) {
            if (airtableEndDate.isBefore(airtableStartDate, "day")) {
                return "End date cannot be before the start date.";
            }
            const monthsDiff = airtableEndDate.diff(
                airtableStartDate,
                "month",
                true
            );
            if (monthsDiff > MAX_RANGE_MONTHS) {
                return "Date range cannot be larger than 6 months.";
            }
        }
        return null;
    };

    const handleOpenFetchDialog = () => {
        setAirtableStartDate(null);
        setAirtableEndDate(null);
        setFetchError(null);
        setFetchDialogOpen(true);
    };

    const handleCloseFetchDialog = () => {
        setFetchDialogOpen(false);
        setFetchError(null);
    };

    const handlePreviewAirtable = async () => {
        const err = validateAirtableDates();
        if (err) {
            setFetchError(err);
            return;
        }
        setFetchError(null);
        if (!airtableStartDate) return;

        const end =
            airtableEndDate?.isValid()
                ? airtableEndDate
                : airtableStartDate;
        const startStr = airtableStartDate.format("YYYY-MM-DD");
        const endStr = end.format("YYYY-MM-DD");

        setAirtableLoading(true);
        try {
            const rows = await fetchTitlesFromAirtable(startStr, endStr);
            setPreviewStartDate(startStr);
            setPreviewEndDate(endStr);
            setPreviewRows(rows);
            setFetchDialogOpen(false);
            setPreviewDialogOpen(true);
        } catch (e) {
            console.error("Failed to fetch titles from Airtable", e);
            setFetchError("Failed to fetch titles. Please try again.");
            enqueueSnackbar("Failed to fetch titles from Airtable.", {
                variant: "error",
            });
        } finally {
            setAirtableLoading(false);
        }
    };

    const handleClosePreviewDialog = () => {
        setPreviewDialogOpen(false);
        setPreviewRows([]);
        setPreviewStartDate("");
        setPreviewEndDate("");
    };

    const handleDownloadEmaAvailsFromApi = async (
        startStr: string,
        endStr: string
    ) => {
        setDownloadLoading(true);
        try {
            const { blob, headers } = await downloadEmaAvailsSpreadsheet(
                startStr,
                endStr
            );
            const filename =
                getDownloadFilenameFromHeaders(headers) ||
                `ema-avails-${dayjs().format("YYYY-MM-DD-HHmm")}.xlsx`;
            downloadBlob(blob, filename);
            enqueueSnackbar("EMA Avails generated.", {
                variant: "success",
            });
            setFetchDialogOpen(false);
            setPreviewDialogOpen(false);
            setPreviewRows([]);
        } catch (e) {
            console.error("Failed to generate EMA Avails", e);
            enqueueSnackbar("Failed to generate EMA Avails.", {
                variant: "error",
            });
        } finally {
            setDownloadLoading(false);
        }
    };

    return (
        <div className="config-heading w-80">
            <div>
                {/* Header section starts here */}
                <div
                    className="ingest-header-refresh"
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 16,
                    }}
                >
                    <h3>Avails Management</h3>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AirtableLogoIcon />}
                        onClick={handleOpenFetchDialog}
                    >
                        Fetch new Titles
                    </Button>
                </div>
                <div className="d-flex mb-2 align-item-center">
                    <div className="w-40">
                        <TextField
                            name="searchKey"
                            fullWidth
                            placeholder="Search"
                            variant="outlined"
                            label="Search Keyword"
                            value={searchKey}
                            onChange={handleSearch}
                            onKeyDown={handleKeyDown}
                            InputProps={{
                                endAdornment: (
                                    <>
                                        {searchKey && (
                                            <HighlightOffIcon
                                                className="cursor-pointer"
                                                onClick={() => clearSearch()}
                                            />
                                        )}
                                        <SearchIcon
                                            className="cursor-pointer ml-1"
                                            onClick={() => onSearchAPIcall()}
                                        />
                                    </>
                                ),
                            }}
                        />
                    </div>
                    {loader && <CircularProgress className="ml-1" size={30} />}
                </div>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: 2,
                            mb: 2,
                        }}
                    >
                        <TextField
                            size="small"
                            label="Playback Date Range"
                            placeholder="Select start and end date"
                            value={getFilterDateRangeLabel()}
                            onClick={(e) =>
                                setDateRangeAnchorEl(
                                    e.currentTarget as HTMLElement
                                )
                            }
                            InputProps={{
                                readOnly: true,
                            }}
                            sx={{ minWidth: 260 }}
                        />
                        <TextField
                            size="small"
                            label="Content Licensor"
                            placeholder="Studio / content licensor"
                            value={contentLicensor}
                            onChange={(e) =>
                                setContentLicensor(e.target.value)
                            }
                            onKeyDown={(e) => {
                                if (e.key === "Enter") applyFilters();
                            }}
                            sx={{ minWidth: 200 }}
                        />
                        <Button
                            variant="contained"
                            size="medium"
                            onClick={applyFilters}
                        >
                            Apply filters
                        </Button>
                        <Button
                            variant="outlined"
                            size="medium"
                            onClick={clearFilters}
                        >
                            Clear filters
                        </Button>
                    </Box>

                    <Popover
                        open={Boolean(dateRangeAnchorEl)}
                        anchorEl={dateRangeAnchorEl}
                        onClose={() => setDateRangeAnchorEl(null)}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                        }}
                    >
                        <Box
                            sx={{
                                p: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "stretch",
                                    gap: 1.5,
                                }}
                            >
                                <DateCalendar
                                    value={filterDateTo ?? filterDateFrom}
                                    onChange={handleRangeCalendarChange}
                                    slots={{ day: RangeDay }}
                                    showDaysOutsideCurrentMonth
                                />
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => {
                                        setFilterDateFrom(null);
                                        setFilterDateTo(null);
                                    }}
                                >
                                    Clear
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => {
                                        setDateRangeAnchorEl(null);
                                        applyFilters();
                                    }}
                                >
                                    Apply
                                </Button>
                            </Box>
                        </Box>
                    </Popover>
                </LocalizationProvider>
                {noResult && (
                    <Typography variant="h6">No results found</Typography>
                )}

                {/* Header section ends here */}

                <div>
                    <TableContainer component={Paper}>
                        <Table aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center">
                                        <h3>#</h3>
                                    </TableCell>

                                    <TableCell width={500} align="left">
                                        {renderSortLabel("name", "Name")}
                                    </TableCell>
                                    <TableCell align="center">
                                        {renderSortLabel(
                                            "reference_id",
                                            "Ref ID"
                                        )}
                                    </TableCell>

                                    <TableCell align="center">
                                        {renderSortLabel(
                                            "content_type",
                                            "Content Type"
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {renderSortLabel(
                                            "playback_start_date",
                                            "Playback Start Date"
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {renderSortLabel(
                                            "playback_end_date",
                                            "Playback End Date"
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {renderSortLabel(
                                            "coming_soon_date",
                                            "Coming Soon Date"
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {renderSortLabel(
                                            "created_at",
                                            "Created Date"
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {renderSortLabel("state", "Status")}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {contentList?.map(
                                    (row: any, index: number) => (
                                            <TableRow
                                                key={row.id}
                                                sx={{
                                                    "&:last-child td, &:last-child th":
                                                        { border: 0 },
                                                }}
                                            >
                                                <TableCell align="center">
                                                    {skip + index + 1}
                                                </TableCell>

                                                <TableCell
                                                    onClick={() =>
                                                        handleSelectRecord(row)
                                                    }
                                                    width={500}
                                                    className="breadcrumbs"
                                                    align="left"
                                                >
                                                    <div>
                                                        <div>
                                                            {row?.name || "-"}
                                                        </div>
                                                        {row?.id && (
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                    mt: 0.5,
                                                                }}
                                                            >
                                                                {row.id}
                                                            </Typography>
                                                        )}
                                                    </div>
                                                </TableCell>

                                                <TableCell align="center">
                                                    {row?.reference_id || "-"}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {row?.content_type || "-"}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {(row?.playback_start_date &&
                                                        MILLIS_TO_LOCALE_DIGIT_FORMAT(
                                                            row.playback_start_date
                                                        )) ||
                                                        "-"}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {(row?.playback_end_date &&
                                                        MILLIS_TO_LOCALE_DIGIT_FORMAT(
                                                            row.playback_end_date
                                                        )) ||
                                                        "-"}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {(row?.coming_soon_date &&
                                                        MILLIS_TO_LOCALE_DIGIT_FORMAT(
                                                            row.coming_soon_date
                                                        )) ||
                                                        "-"}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {(row?.created_at &&
                                                        MILLIS_TO_LOCALE_DIGIT_FORMAT(
                                                            row.created_at
                                                        )) ||
                                                        "-"}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        color: getStateColor(
                                                            row?.state
                                                        ),
                                                    }}
                                                >
                                                    {row?.state || "-"}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[25, 50, 100]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={Math.floor(skip / rowsPerPage)}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </div>
                <div ref={metadataFomRef}>
                    <MetadataForm
                        formValue={formValue}
                        refreshList={refreshList}
                    />
                </div>

                {/* Fetch new titles from Airtable dialog */}
                <Dialog
                    open={fetchDialogOpen}
                    onClose={handleCloseFetchDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        Fetch new titles into Wonder Project
                    </DialogTitle>
                    <DialogContent>
                        <Typography sx={{ mb: 2 }}>
                            Please set the date range for titles to fetch into
                            the system.
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                }}
                            >
                                <DatePicker
                                    label="Start date"
                                    value={airtableStartDate}
                                    onChange={(v) => {
                                        setAirtableStartDate(v);
                                        setFetchError(null);
                                    }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            required: true,
                                        },
                                    }}
                                />
                                <DatePicker
                                    label="End date (optional)"
                                    value={airtableEndDate}
                                    minDate={airtableStartDate ?? undefined}
                                    onChange={(v) => {
                                        setAirtableEndDate(v);
                                        setFetchError(null);
                                    }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                        },
                                    }}
                                />
                                {fetchError && (
                                    <Typography color="error" variant="body2">
                                        {fetchError}
                                    </Typography>
                                )}
                            </Box>
                        </LocalizationProvider>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={handleCloseFetchDialog}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handlePreviewAirtable}
                            disabled={airtableLoading || downloadLoading}
                        >
                            {airtableLoading ? "Loading…" : "Preview"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Preview Airtable titles dialog */}
                <Dialog
                    open={previewDialogOpen}
                    onClose={handleClosePreviewDialog}
                    maxWidth="lg"
                    fullWidth
                >
                    <DialogTitle>
                        Preview: titles in date range
                    </DialogTitle>
                    <DialogContent>
                        <TableContainer
                            component={Paper}
                            variant="outlined"
                            sx={{ maxHeight: 440 }}
                        >
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <strong>RefId</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Name</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Content type</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Start date</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>End date</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {previewRows.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={5}
                                                align="center"
                                                sx={{ py: 3 }}
                                            >
                                                No titles in this date range.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        previewRows.map((row) => (
                                            <TableRow
                                                key={`${row.refId}-${row.startDate}-${row.endDate}`}
                                            >
                                                <TableCell>
                                                    {row.refId}
                                                </TableCell>
                                                <TableCell>
                                                    {row.name}
                                                </TableCell>
                                                <TableCell>
                                                    {row.contentType}
                                                </TableCell>
                                                <TableCell>
                                                    {row.startDate}
                                                </TableCell>
                                                <TableCell>
                                                    {row.endDate}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={handleClosePreviewDialog}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() =>
                                handleDownloadEmaAvailsFromApi(
                                    previewStartDate,
                                    previewEndDate
                                )
                            }
                            disabled={
                                !previewStartDate ||
                                !previewEndDate ||
                                downloadLoading
                            }
                        >
                            {downloadLoading
                                ? "Generating…"
                                : "Generate EMA Avails"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        </div>
    );
};

export default MetadataManagement;
